/* eslint-disable */
const BULLET_PREFIX = /^\s*[•●◦▪‣]\s+/
const INLINE_MARKDOWN_REGEX = /\*([^\s*](?:[^*]*[^\s*])?)\*/g
const BLOCK_TAGS = new Set(['P', 'DIV'])
const INLINE_SKIP_TAGS = new Set(['A', 'CODE', 'PRE', 'EM', 'STRONG', 'S', 'DEL', 'KBD'])
const PRESERVE_RICH_HTML_REGEX = /<(a|img|table|thead|tbody|tr|td|th|pre|code|blockquote)\b/i
const GMAIL_HTML_REGEX = /(gmail_signature|data-smartmail=|<div dir="ltr")/i

const escapeHtml = (value = '') => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const hasSkippedInlineAncestor = (node) => {
    let current = node?.parentElement;

    while (current) {
        if (INLINE_SKIP_TAGS.has(current.tagName)) {
            return true;
        }
        current = current.parentElement;
    }

    return false;
}

const isInlineMarkdownBoundary = (character) => {
    if (!character) return true;
    return /[\s.,!?;:()\[\]{}'"“”‘’«»/\\-]/.test(character);
}

const renderInlineMarkdown = (text = '') => {
    if (!text.includes('*')) {
        return escapeHtml(text);
    }

    let lastIndex = 0;
    let rendered = '';

    INLINE_MARKDOWN_REGEX.lastIndex = 0;

    let match;
    while ((match = INLINE_MARKDOWN_REGEX.exec(text)) !== null) {
        const startIndex = match.index;
        const endIndex = INLINE_MARKDOWN_REGEX.lastIndex;
        const previousCharacter = startIndex === 0 ? '' : text[startIndex - 1];
        const nextCharacter = endIndex >= text.length ? '' : text[endIndex];

        if (!isInlineMarkdownBoundary(previousCharacter) || !isInlineMarkdownBoundary(nextCharacter)) {
            continue;
        }

        rendered += escapeHtml(text.slice(lastIndex, startIndex));
        rendered += `<strong>${escapeHtml(match[1])}</strong>`;
        lastIndex = endIndex;
    }

    rendered += escapeHtml(text.slice(lastIndex));
    return rendered;
}

const replaceMarkdownTextNode = (node, doc) => {
    const text = node?.nodeValue || '';
    if (!text.includes('*')) return;

    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let hasChanges = false;

    INLINE_MARKDOWN_REGEX.lastIndex = 0;

    let match;
    while ((match = INLINE_MARKDOWN_REGEX.exec(text)) !== null) {
        const startIndex = match.index;
        const endIndex = INLINE_MARKDOWN_REGEX.lastIndex;
        const previousCharacter = startIndex === 0 ? '' : text[startIndex - 1];
        const nextCharacter = endIndex >= text.length ? '' : text[endIndex];

        if (!isInlineMarkdownBoundary(previousCharacter) || !isInlineMarkdownBoundary(nextCharacter)) {
            continue;
        }

        if (startIndex > lastIndex) {
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex, startIndex)));
        }

        const emphasis = doc.createElement('strong');
        emphasis.textContent = match[1];
        fragment.appendChild(emphasis);

        lastIndex = endIndex;
        hasChanges = true;
    }

    if (!hasChanges) {
        return;
    }

    if (lastIndex < text.length) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode?.replaceChild(fragment, node);
}

const trimEdgeWhitespace = (element) => {
    const trimTextNode = (node, side) => {
        if (!node || node.nodeType !== Node.TEXT_NODE) {
            return;
        }

        node.nodeValue = side === 'start'
            ? node.nodeValue.replace(/^\s+/, '')
            : node.nodeValue.replace(/\s+$/, '');

        if (!node.nodeValue) {
            node.parentNode?.removeChild(node);
        }
    }

    trimTextNode(element.firstChild, 'start');
    trimTextNode(element.lastChild, 'end');
}

const stripLeadingBullet = (element) => {
    const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let textNode;

    while ((textNode = walker.nextNode())) {
        if (!textNode.nodeValue?.trim()) {
            continue;
        }

        textNode.nodeValue = textNode.nodeValue.replace(BULLET_PREFIX, '').replace(/^\s+/, '');
        if (!textNode.nodeValue) {
            textNode.parentNode?.removeChild(textNode);
        }
        break;
    }
}

const isBulletBlock = (node) => {
    if (!(node instanceof Element)) return false;
    if (!BLOCK_TAGS.has(node.tagName)) return false;
    return BULLET_PREFIX.test(node.textContent || '');
}

const normalizeInlineMarkdown = (root, doc) => {
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            if (!node?.nodeValue?.includes('*')) {
                return NodeFilter.FILTER_REJECT;
            }

            if (hasSkippedInlineAncestor(node)) {
                return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
        },
    });

    const nodes = [];
    let current;
    while ((current = walker.nextNode())) {
        nodes.push(current);
    }

    nodes.forEach((node) => replaceMarkdownTextNode(node, doc));
}

const normalizeBulletBlocks = (root, doc) => {
    const children = Array.from(root.childNodes);

    for (let index = 0; index < children.length; index++) {
        const child = children[index];

        if (!(child instanceof Element)) {
            continue;
        }

        if (isBulletBlock(child)) {
            const list = doc.createElement('ul');
            root.insertBefore(list, child);

            while (index < children.length && isBulletBlock(children[index])) {
                const bulletBlock = children[index];
                const item = doc.createElement('li');
                const clone = bulletBlock.cloneNode(true);

                stripLeadingBullet(clone);
                while (clone.firstChild) {
                    item.appendChild(clone.firstChild);
                }
                trimEdgeWhitespace(item);
                list.appendChild(item);
                root.removeChild(bulletBlock);
                index += 1;
            }

            index -= 1;
            continue;
        }

        normalizeBulletBlocks(child, doc);
    }
}

const trimBlockWhitespace = (root) => {
    root.querySelectorAll('p, div, li').forEach((element) => trimEdgeWhitespace(element));
}

const shouldFormatStructuredText = (text = '', html = '') => {
    if (!text) return false;
    if (PRESERVE_RICH_HTML_REGEX.test(html || '')) return false;

    return /\n/.test(text) || BULLET_PREFIX.test(text) || INLINE_MARKDOWN_REGEX.test(text);
}

export const normalizeTextEditorHtml = (html = '') => {
    if (!html || typeof DOMParser === 'undefined') {
        return html || '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const { body } = doc;

    normalizeInlineMarkdown(body, doc);
    normalizeBulletBlocks(body, doc);
    trimBlockWhitespace(body);

    return body.innerHTML;
}

const normalizeSpace = (value = '') => value.replace(/\u00a0/g, ' ');

const cleanInlineHtml = (value = '') => value
    .replace(/(?:\s*<br>\s*)+$/g, '')
    .replace(/^(?:\s*<br>\s*)+/g, '')
    .replace(/\s+/g, ' ')
    .replace(/ ?<br> ?/g, '<br>')
    .trim();

const serializeInlineNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        return escapeHtml(normalizeSpace(node.textContent || ''));
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const tagName = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map((childNode) => serializeInlineNode(childNode)).join('');

    if (tagName === 'br') {
        return '<br>';
    }

    if (tagName === 'b' || tagName === 'strong') {
        return `<strong>${children}</strong>`;
    }

    if (tagName === 'i' || tagName === 'em') {
        return `<em>${children}</em>`;
    }

    if (tagName === 'a') {
        const href = node.getAttribute('href');
        const target = node.getAttribute('target');
        const rel = node.getAttribute('rel');
        const hrefAttribute = href ? ` href="${escapeHtml(href)}"` : '';
        const targetAttribute = target ? ` target="${escapeHtml(target)}"` : '';
        const relAttribute = rel ? ` rel="${escapeHtml(rel)}"` : '';
        return `<a${hrefAttribute}${targetAttribute}${relAttribute}>${children}</a>`;
    }

    return children;
}

const serializeParagraphFromNodes = (nodes = []) => {
    const html = cleanInlineHtml(nodes.map((node) => serializeInlineNode(node)).join(''));
    return html ? `<p>${html}</p>` : '';
}

const serializeListItemNode = (listItemNode, nestedLists = []) => {
    const inlineNodes = [];
    const childNestedLists = [];

    Array.from(listItemNode.childNodes).forEach((childNode) => {
        if (childNode.nodeType === Node.ELEMENT_NODE && /^(UL|OL)$/i.test(childNode.tagName)) {
            childNestedLists.push(serializeListNode(childNode));
            return;
        }

        inlineNodes.push(childNode);
    });

    const inlineHtml = cleanInlineHtml(inlineNodes.map((node) => serializeInlineNode(node)).join(''));
    const nestedHtml = [...childNestedLists, ...nestedLists].join('');
    return `<li>${inlineHtml}${nestedHtml}</li>`;
}

function serializeListNode(listNode) {
    const tagName = listNode.tagName.toLowerCase();
    const items = [];
    let currentListItem = null;
    let trailingLists = [];

    Array.from(listNode.childNodes).forEach((childNode) => {
        if (childNode.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (childNode.tagName === 'LI') {
            if (currentListItem) {
                items.push(serializeListItemNode(currentListItem, trailingLists));
            }

            currentListItem = childNode;
            trailingLists = [];
            return;
        }

        if (/^(UL|OL)$/i.test(childNode.tagName) && currentListItem) {
            trailingLists.push(serializeListNode(childNode));
        }
    });

    if (currentListItem) {
        items.push(serializeListItemNode(currentListItem, trailingLists));
    }

    return items.length ? `<${tagName}>${items.join('')}</${tagName}>` : '';
}

function serializeContainerNode(containerNode) {
    const blocks = [];
    let inlineNodes = [];

    const flushInlineNodes = () => {
        const paragraphHtml = serializeParagraphFromNodes(inlineNodes);
        if (paragraphHtml) {
            blocks.push(paragraphHtml);
        }
        inlineNodes = [];
    };

    Array.from(containerNode.childNodes).forEach((childNode) => {
        if (childNode.nodeType === Node.TEXT_NODE) {
            inlineNodes.push(childNode);
            return;
        }

        if (childNode.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (/^(UL|OL)$/i.test(childNode.tagName)) {
            flushInlineNodes();
            const listHtml = serializeListNode(childNode);
            if (listHtml) {
                blocks.push(listHtml);
            }
            return;
        }

        if (/^(DIV|P)$/i.test(childNode.tagName)) {
            flushInlineNodes();
            blocks.push(...serializeContainerNode(childNode));
            return;
        }

        inlineNodes.push(childNode);
    });

    flushInlineNodes();
    return blocks;
}

export const convertTextEditorPasteHtmlToHtml = (html = '') => {
    if (!html || typeof DOMParser === 'undefined') {
        return html || '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const { body } = doc;

    body.querySelectorAll('.gmail_signature,[data-smartmail="gmail_signature"]').forEach((element) => element.remove());

    return serializeContainerNode(body).join('');
}

export const convertTextEditorPasteTextToHtml = (text = '') => {
    if (!text) {
        return '';
    }

    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let listItems = [];

    const flushList = () => {
        if (!listItems.length) {
            return;
        }

        blocks.push(`<ul>${listItems.join('')}</ul>`);
        listItems = [];
    };

    lines.forEach((line) => {
        const normalizedLine = normalizeSpace(line);
        const trimmedLine = normalizedLine.trim();

        if (!trimmedLine) {
            flushList();
            return;
        }

        const bulletMatch = normalizedLine.match(BULLET_PREFIX);
        if (bulletMatch) {
            const bulletText = normalizedLine.replace(BULLET_PREFIX, '').trim();
            listItems.push(`<li>${renderInlineMarkdown(bulletText)}</li>`);
            return;
        }

        flushList();
        blocks.push(`<p>${renderInlineMarkdown(trimmedLine)}</p>`);
    });

    flushList();
    return blocks.join('');
}

export const formatTextEditorPaste = (text = '', html = '') => {
    if (GMAIL_HTML_REGEX.test(html || '')) {
        const structuredHtml = convertTextEditorPasteHtmlToHtml(html);
        return {
            text,
            html: structuredHtml || html || '',
        };
    }

    if (shouldFormatStructuredText(text, html)) {
        const structuredHtml = convertTextEditorPasteTextToHtml(text);
        return {
            text,
            html: structuredHtml || html || '',
        };
    }

    return {
        text,
        html: normalizeTextEditorHtml(html || ''),
    };
}
